import React, { useState, useEffect, useContext, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import ContentLayoutContext from 'components/layouts/ContentLayoutContextProvider'
import { useParams } from 'react-router-dom'
import { Grid, Stack } from '@mui/material'
import FinishedGoodsGrid from 'components/generic/datagrid/FinishedGoodsGrid'
import InventorySummaryBar from 'components/layouts/InventorySummaryBar'
import { useGetFinishedGoodsQuery, useGetInventoryFinishedGoodListQuery } from 'services/finishedGoods'
import { showFormModal } from 'state/ui'
import FORM_MODALS from 'utils/ui/modals'
import { COMMON_STATUSES, MODAL_VIEW_MODES, SORTABLE_FINISHED_GOOD_FIELDS, PAGE_SIZE } from 'utils/constants'
import { parseNumberObject } from 'utils/helpers'
import { dateTimeFormatter } from 'utils/data-manipulation/valueFormatter'

const FinishedGoodsListContainer = () => {
  const dispatch = useDispatch()

  const [queryOptions, setQueryOptions] = useState({
    filter: {
      search: '',
      sort: {
        field: SORTABLE_FINISHED_GOOD_FIELDS.TAG,
        ascending: false,
      },
      statuses: [COMMON_STATUSES.AVAILABLE.id, COMMON_STATUSES.UNAVAILABLE.id],
    },
    page: 1,
    pageSize: PAGE_SIZE,
  })

  const { data: finishedGoodsList, isFetching: isFetchingFinishedGoodsList } = useGetFinishedGoodsQuery({
    ...queryOptions,
    filter: JSON.stringify(queryOptions.filter),
  })
  const { data: metaData, isLoading: isMetaDataLoading } = useGetInventoryFinishedGoodListQuery({})
  const isLoading = isFetchingFinishedGoodsList || isMetaDataLoading
  const { id } = useParams()
  const context = useContext(ContentLayoutContext)

  const { defaultYardTimezone } = useSelector(state => state.auth)

  // modalShown is used to make sure that the modal only displays once per instance
  // NOTE: This only works because the FG route currently uses the FG's ID in the url, causing the entire page to rerender when it changes
  // If we ever update the FG route to stop using the ID in the url, we should revisit this
  const [modalShown, setModalShown] = useState(false)

  useEffect(() => {
    context.setContainerTitle('Finished Goods')
  }, [])

  useEffect(() => {
    if (id && !isLoading && !modalShown) {
      dispatch(
        showFormModal({
          formModalName: FORM_MODALS.NEW_FINISHED_GOOD,
          formModalProps: {
            viewMode: MODAL_VIEW_MODES.VIEW,
            navigateTo: '/app/inventory/finished-goods',
          },
        }),
      )
      setModalShown(true)
    }
  }, [id, isLoading, modalShown])

  const summaryItems = useMemo(
    () => [
      {
        label: 'FINISHED GOODS (AVAILABLE)',
        value: `${parseNumberObject(metaData?.finishedGoodsAvailable?.weight, { format: 'weight' })}`,
      },
      {
        label: 'TOTAL WEIGHT',
        value: `${parseNumberObject(metaData?.finishedGoodsTotal?.weight, { format: 'weight' })}`,
      },
      {
        label: 'LARGEST MATERIAL BY WEIGHT',
        value: metaData?.largestFinishedGoodMaterialByWeight,
      },
    ],
    [metaData],
  )

  const transformedRows = (finishedGoodsList?.Items || [])
    .filter(good => good.status?.value !== COMMON_STATUSES.VOID.id && good.status?.value !== COMMON_STATUSES.SHIPPED.id)
    .map(good => ({
      ...good,
      id: good?.id,
      tag: good?.tag,
      effectiveDate: dateTimeFormatter(good?.effectiveDate, { timeZone: defaultYardTimezone }),
      commodity: good?.materialType?.commodityName,
      material: good?.materialType?.commonName,
      netWeight: good?.weight?.net,
      itemCount: good?.itemCount,
      status: good?.status?.value,
      purchaseOrderNumber: good?.purchaseOrderNumber,
    }))

  return (
    <Stack>
      <InventorySummaryBar items={summaryItems} />
      <Grid p={3} sx={{ height: '100%' }} container flexDirection="column">
        <FinishedGoodsGrid
          rows={transformedRows}
          loading={isFetchingFinishedGoodsList}
          filter={queryOptions.filter}
          setFilter={newFilter => setQueryOptions(prevState => ({ ...prevState, filter: newFilter }))}
          rowCount={finishedGoodsList?.totalResultsCount || 0}
          paginationMode="server"
          useBackendSortAndFilter
          onPageChange={newPageNumber => setQueryOptions(prevState => ({ ...prevState, page: newPageNumber + 1 }))}
          onPageSizeChange={newPageSize => setQueryOptions(prevState => ({ ...prevState, pageSize: newPageSize }))}
          autoPageSize={false}
          pageSize={queryOptions.pageSize}
        />
      </Grid>
    </Stack>
  )
}

export default FinishedGoodsListContainer
